import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/axiosClient";
import { LogoMark } from "../components/client/kzIcons"; // adjust the path if this file lives elsewhere

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.rp {
  --ink: #0c0c0b;
  --muted: #6b6b66;
  --line: #e6e6e1;
  --fill: #f3f3ef;
  --accent: #fff000;
  --bad: #a8201a;

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: #f6f6f3;
  color: var(--ink);
  font-family: 'Plus Jakarta Sans', 'Inter', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.rp *, .rp *::before, .rp *::after { box-sizing: border-box; }
.rp :focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.rp-card { width: 100%; max-width: 420px; background: #fff; border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.rp-head { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 28px 32px 22px; border-bottom: 1px solid var(--line); text-align: center; }
.rp-brand { display: flex; align-items: center; gap: 10px; font-size: 1.05rem; font-weight: 700; letter-spacing: -.01em; }
.rp-title { margin: 0; font-size: 1.5rem; font-weight: 700; letter-spacing: -.02em; line-height: 1.2; }
.rp-sub { margin: 6px 0 0; color: var(--muted); font-size: 14px; }

.rp-body { padding: 24px 32px 28px; }
.rp-field + .rp-field { margin-top: 18px; }
.rp-label { display: block; margin-bottom: 8px; font-size: 14px; font-weight: 600; }
.rp-input-wrap { position: relative; }
.rp-input {
  display: block; width: 100%; height: 50px; padding: 0 48px 0 14px;
  border: 2px solid transparent; border-radius: 8px; background: var(--fill);
  color: var(--ink); font: inherit; font-size: 16px;
}
.rp-input:focus { outline: none; border-color: var(--ink); background: #fff; }
.rp-input:disabled { opacity: .6; }
.rp-toggle { position: absolute; top: 50%; right: 6px; transform: translateY(-50%); padding: 8px 10px; border: 0; border-radius: 6px; background: transparent; color: var(--muted); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.rp-toggle:hover { color: var(--ink); background: var(--fill); }
.rp-help { margin: 8px 0 0; color: var(--muted); font-size: 13px; }

.rp-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 50px; margin-top: 22px; border: 0; border-radius: 8px; background: var(--accent); color: #000; font: inherit; font-size: 16px; font-weight: 700; cursor: pointer; transition: background .15s; }
.rp-btn:hover:not(:disabled) { background: #ffe600; }
.rp-btn:disabled { background: var(--fill); color: #9a9a94; cursor: not-allowed; }
.rp-spin { width: 16px; height: 16px; border: 2px solid rgba(0,0,0,.2); border-top-color: #000; border-radius: 50%; animation: rp-spin .7s linear infinite; }
@keyframes rp-spin { to { transform: rotate(360deg); } }

.rp-note { margin-top: 18px; padding: 12px 14px; border-radius: 8px; font-size: 14px; font-weight: 500; line-height: 1.5; }
.rp-note.ok { background: #fffbc7; color: var(--ink); }
.rp-note.err { background: #fdeceb; color: var(--bad); }

.rp-back { display: block; margin-top: 20px; text-align: center; color: var(--ink); font-size: 14px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #c9c9c2; }
.rp-back:hover { text-decoration-color: var(--ink); }

@media (max-width: 480px) {
  .rp-head { padding: 24px 20px 18px; }
  .rp-body { padding: 20px 20px 24px; }
}
@media (prefers-reduced-motion: reduce) { .rp-spin { animation: none; } }
`;

function firstError(data) {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  const first = Object.values(data)[0];
  return Array.isArray(first) ? first[0] : typeof first === "string" ? first : "";
}

function ResetPasswordPage() {
  const { uid = "", token = "" } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const linkInvalid = !token || !uid;
  const done = !!message;

  useEffect(() => {
    if (linkInvalid) setError("This reset link is invalid or has expired.");
  }, [linkInvalid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (linkInvalid) return setError("This reset link is invalid or has expired.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");

    setLoading(true);
    try {
      await api.post(`/password-reset/${uid}/${token}/`, { password });
      setMessage("Password reset successful. Redirecting you to log in…");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(firstError(err.response?.data) || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || linkInvalid || done;
  const type = show ? "text" : "password";

  return (
    <div className="rp">
      <style>{CSS}</style>

      <div className="rp-card">
        <header className="rp-head">
          <div className="rp-brand"><LogoMark size={28} />KaaKazini</div>
          <div>
            <h1 className="rp-title">Set a new password</h1>
            <p className="rp-sub">Choose something you haven't used before.</p>
          </div>
        </header>

        <div className="rp-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="rp-field">
              <label className="rp-label" htmlFor="rp-password">New password</label>
              <div className="rp-input-wrap">
                <input
                  id="rp-password"
                  className="rp-input"
                  type={type}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={disabled}
                />
                <button
                  type="button"
                  className="rp-toggle"
                  onClick={() => setShow((v) => !v)}
                  aria-pressed={show}
                  aria-label={show ? "Hide passwords" : "Show passwords"}
                >
                  {show ? "Hide" : "Show"}
                </button>
              </div>
              <p className="rp-help">At least 8 characters.</p>
            </div>

            <div className="rp-field">
              <label className="rp-label" htmlFor="rp-confirm">Confirm password</label>
              <input
                id="rp-confirm"
                className="rp-input"
                type={type}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={disabled}
              />
            </div>

            <button type="submit" className="rp-btn" disabled={disabled}>
              {loading ? (<><span className="rp-spin" />Resetting</>) : "Reset password"}
            </button>
          </form>

          {message && <div className="rp-note ok" role="status">{message}</div>}
          {error && <div className="rp-note err" role="alert">{error}</div>}

          <Link to="/login" className="rp-back">Back to log in</Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
